<template>
  <div class="ps-customer" :class="{ 'ps-customer--clean': clean }">
    <!-- Tabs: search customer (v2 numpad) / add customer form -->
    <div class="ps-customer-tabs" role="tablist">
      <button
        type="button"
        class="ps-customer-tab"
        :class="{ 'is-active': tab === 'customer' }"
        role="tab"
        :aria-selected="tab === 'customer' ? 'true' : 'false'"
        @click="switchTab('customer')"
      >
        <v-icon size="16">fal fa-search</v-icon>
        {{ $t("search_customer") }}
      </button>
      <button
        type="button"
        class="ps-customer-tab"
        :class="{ 'is-active': tab === 'add_customer' }"
        role="tab"
        :aria-selected="tab === 'add_customer' ? 'true' : 'false'"
        @click="switchTab('add_customer')"
      >
        <v-icon size="16">fal fa-user-plus</v-icon>
        {{ $t("add_customer") }}
      </button>
    </div>

    <!-- ============ SEARCH TAB — customer list + search ============ -->
    <div v-if="tab === 'customer'" class="ps-customer-search">
      <!-- search box -->
      <div class="ps-customer-searchbox">
        <v-icon size="16" class="ps-customer-searchbox__icon"
          >fal fa-search</v-icon
        >
        <input
          v-model="query"
          type="text"
          class="ps-customer-searchbox__input"
          :placeholder="$t('search_customer')"
          @input="onQueryInput"
        />
        <button
          v-if="query"
          type="button"
          class="ps-customer-searchbox__clear"
          :aria-label="$t('clear')"
          @click="clearQuery"
        >
          <v-icon size="14">fal fa-times</v-icon>
        </button>
      </div>

      <!-- loading / empty feedback -->
      <p v-if="loading" class="ps-customer-hint">{{ $t("searching") }}</p>

      <!-- results list -->
      <div class="ps-customer-results thin-scroll">
        <button
          v-for="c in results"
          :key="c.id"
          type="button"
          class="ps-customer-result"
          :class="{ 'is-active': selectedCustomerId === c.id }"
          @click="selectCustomer(c)"
        >
          <span class="ps-customer-result__avatar">{{
            initials(c.first_name || c.name)
          }}</span>
          <span class="ps-customer-result__body">
            <span class="ps-customer-result__name">{{ customerName(c) }}</span>
            <span class="ps-customer-result__phone">{{
              c.phone_no || c.phone
            }}</span>
          </span>
          <v-icon
            v-if="selectedCustomerId === c.id"
            size="16"
            color="#5b39c9"
            class="ps-customer-result__check"
            >fa-solid fa-check</v-icon
          >
        </button>
      </div>
      <p
        v-if="!loading && !results.length"
        class="ps-customer-hint ps-customer-hint--error"
      >
        {{ $t("no_data") }}
      </p>

      <button
        type="button"
        class="ps-customer-walkin"
        @click="selectWalkIn"
      >
        <v-icon size="16" color="#5b39c9">fal fa-plus</v-icon>
        <span class="ps-customer-walkin__body">
          <span class="ps-customer-walkin__title">{{
            $t("walk_in_customer")
          }}</span>
          <span class="ps-customer-walkin__hint">{{
            $t("walk_in_customer_hint")
          }}</span>
        </span>
      </button>
    </div>

    <!-- ============ ADD CUSTOMER TAB ============ -->
    <div v-else class="ps-customer-add">
      <v-form @submit.prevent="saveCustomer" class="ps-customer-form">
        <div class="ps-customer-section">
          <div class="ps-customer-section__head">
            <span class="ps-customer-section__title">
              <v-icon small left>fal fa-user</v-icon>
              {{ $t("personal_information") }}</span
            >
          </div>
          <v-row class="mt-3">
            <v-col cols="12" md="6">
              <TextField v-model="form.first_name" label="full_name" />
            </v-col>
            <v-col cols="12" md="6">
              <TextField
                v-model="form.phone_no"
                label="phone_no"
                :maxlength="10"
              />
            </v-col>
          </v-row>
        </div>

        <div class="ps-customer-section">
          <div class="ps-customer-section__head">
            <span class="ps-customer-section__title">
              <v-icon small left>fal fa-truck</v-icon>
              {{ $t("logistics_partner") }}
            </span>
          </div>
          <v-row class="mt-3">
            <v-col
              cols="12"
              md="3"
              v-for="item in (Array.isArray(logistics)
                ? logistics
                : logistics?.data || []
              ).filter((i) => i)"
              :key="item.id"
            >
              <v-card
                class="logistic-option"
                :class="{ selected: form.logistics_id === item.id }"
                elevation="0"
                outlined
                @click="form.logistics_id = item.id"
              >
                <div class="logistic-content">
                  <v-avatar size="56" class="mb-2">
                    <v-img :src="item.logo" alt="logo" />
                  </v-avatar>
                  <div class="logistic-title">{{ item.name }}</div>
                  <div class="check" v-if="form.logistics_id === item.id">
                    <v-icon x-small color="white">fa-solid fa-check</v-icon>
                  </div>
                </div>
              </v-card>
            </v-col>
          </v-row>
        </div>

        <div class="ps-customer-section">
          <div class="ps-customer-section__head">
            <span class="ps-customer-section__title">
              <v-icon small left>fal fa-map-marker-alt</v-icon>
              {{ $t("location_information") }}
            </span>
          </div>
          <v-row class="mt-3">
            <v-col cols="12" md="6">
              <TextSelect
                v-model="form.province_id"
                :items="
                  Array.isArray(provinces) ? provinces : provinces?.data || []
                "
                itemValue="pr_id"
                text="pr_name"
                label="province"
              />
            </v-col>
            <v-col cols="12" md="6">
              <TextSelect
                v-model="form.district_id"
                :items="
                  Array.isArray(districts) ? districts : districts?.data || []
                "
                itemValue="dr_id"
                text="dr_name"
                label="district"
              />
            </v-col>
            <v-col cols="12" md="6" class="pt-0">
              <TextSelect
                v-model="form.village_id"
                :items="
                  Array.isArray(villages) ? villages : villages?.data || []
                "
                itemValue="vill_id"
                text="vill_name"
                label="village"
              />
            </v-col>
          </v-row>
        </div>

        <div class="ps-customer-section">
          <div class="ps-customer-section__head">
            <span class="ps-customer-section__title">
              <v-icon small left>fal fa-map-marker-alt</v-icon>
              {{ $t("detailed_address") }}
            </span>
          </div>
          <v-row class="mt-3">
            <v-col cols="12">
              <app-text-area v-model="form.address" label="noted" />
            </v-col>
          </v-row>
        </div>

        <div class="ps-customer-form-actions">
          <button
            type="button"
            class="ps-customer-btn ps-customer-btn--clear"
            @click="clearForm"
          >
            {{ $t("clear") }}
          </button>
          <button type="submit" class="ps-customer-btn ps-customer-btn--save">
            <v-icon size="15" left>fal fa-user-plus</v-icon>
            {{ $t("add_customer") }}
          </button>
        </div>
      </v-form>
    </div>
  </div>
</template>

<script>
import TextField from "@/components/global/TextField";
import TextSelect from "@/components/global/TextSelect";
import { mapGetters, mapMutations, mapActions } from "vuex";
import { toast } from "@/controllers/alert";
import AppTextArea from "@/components/global/TextArea";

export default {
  components: {
    TextField,
    TextSelect,
    AppTextArea,
  },
  props: {
    clean: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      tab: "customer",
      query: "",
      loading: false,
      results: [],
      timer: null,
      selectedCustomerId: null,
      form: {
        first_name: "",
        phone_no: "20",
        address: "",
        province_id: "",
        district_id: "",
        village_id: "",
        logistics_id: "",
      },
    };
  },
  mounted() {
    this.selectedCustomerId = this.customer_id;
    this.fetchLogistics(this);
    this.fetchProvinces(this);
    this.fetchCustomers();
  },
  beforeDestroy() {
    if (this.timer) clearTimeout(this.timer);
  },
  watch: {
    "form.province_id": function (val) {
      if (val) {
        const params = {
          that: this,
          pr_id: val.pr_id,
        };
        this.fetchDistricts(params);
      }
    },
    "form.district_id": function (val) {
      if (val) {
        const params = {
          that: this,
          dr_id: val.dr_id,
        };
        this.fetchVillages(params);
      }
    },
  },

  computed: {
    ...mapGetters({
      logistics: "index/logistics",
      provinces: "index/provinces",
      districts: "index/districts",
      villages: "index/villages",
      customer_id: "index/customer_id",
    }),
  },
  methods: {
    ...mapMutations({
      SET_CUSTOMER_ID: "index/SET_CUSTOMER_ID",
    }),
    ...mapActions({
      fetchLogistics: "index/fetchLogistics",
      fetchProvinces: "index/fetchProvinces",
      fetchDistricts: "index/fetchDistricts",
      fetchVillages: "index/fetchVillages",
    }),

    switchTab(tab) {
      this.tab = tab;
      if (tab === "customer" && !this.results.length && !this.loading) {
        this.fetchCustomers();
      }
    },

    // ---------- customer list search ----------
    onQueryInput() {
      this.fetchCustomers();
    },
    clearQuery() {
      this.query = "";
      this.fetchCustomers();
    },
    // Debounced fetch — empty query loads the recent customer list.
    fetchCustomers() {
      if (this.timer) clearTimeout(this.timer);
      const q = this.query.trim();
      this.timer = setTimeout(
        async () => {
          this.loading = true;
          try {
            const { data } = await this.$http.get(
              `customers?limit=50&q=${encodeURIComponent(q)}`
            );
            this.loading = false;
            if (this.query.trim() !== q) return; // stale response
            this.results = Array.isArray(data?.data)
              ? data.data
              : Array.isArray(data?.data?.data)
              ? data.data.data
              : [];
          } catch (error) {
            console.log("fetchCustomers", error);
            this.loading = false;
            this.results = [];
          }
        },
        q ? 300 : 0
      );
    },

    // ---------- selection ----------
    selectCustomer(c) {
      const id = typeof c === "object" ? c.id : c;
      this.selectedCustomerId = id;
      this.SET_CUSTOMER_ID(id);
      this.$emit("selectCustomer", id);
    },
    selectWalkIn() {
      this.selectedCustomerId = null;
      this.SET_CUSTOMER_ID(null);
      this.$emit("selectCustomer", null);
    },

    // ---------- add customer form ----------
    async saveCustomer() {
      try {
        const body = {
          first_name: this.form.first_name,
          phone_no: this.form.phone_no,
          address: this.form.address,
          village_id:
            this.form.village_id?.vill_id || this.form.village_id || null,
          logistics_id:
            (typeof this.form.logistics_id === "object"
              ? this.form.logistics_id?.id
              : this.form.logistics_id) || null,
        };
        let resp;
        resp = await this.$http.post("customers_add", body);
        const { data } = resp;
        if (data.status) {
          this.selectedCustomerId = data.data.id;
          this.SET_CUSTOMER_ID(data.data.id);
          this.tab = "customer";
          this.clearForm();
          this.$emit("selectCustomer", data.data.id);
          await toast(this, data.message, "success");
        }
      } catch (error) {
        console.log(error);
      }
    },
    clearForm() {
      this.form = {
        first_name: "",
        phone_no: "20",
        address: "",
        province_id: "",
        district_id: "",
        village_id: "",
        logistics_id: "",
      };
    },
    initials(name) {
      if (!name) return "?";
      const parts = String(name).trim().split(/\s+/);
      const first = parts[0]?.[0] || "?";
      const last = (parts[1] && parts[1][0]) || "";
      return (first + last).toUpperCase();
    },
    customerName(c) {
      const name = [c.first_name, c.last_name].filter(Boolean).join(" ");
      return name || c.name || c.phone_no || c.phone || c.id;
    },
  },
};
</script>

<style scoped>
/* ---- Tabs (brutal segmented control) ---- */
.ps-customer-tabs {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 10px;
  padding-bottom: 18px;
  margin-bottom: 18px;
  border-bottom: 1px solid #e2e8f0;
}

.ps-customer-tab {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  min-height: 46px;
  padding: 0 12px;
  border-radius: 14px;
  border: 1px solid #e2e8f0;
  background: #ffffff;
  color: #64748b;
  font-size: 14px;
  font-weight: 900;
  cursor: pointer;
  transition: background-color 0.15s ease, color 0.15s ease,
    border-color 0.15s ease, box-shadow 0.15s ease, transform 0.15s ease;
}

.ps-customer-tab:hover {
  color: #5b39c9;
  border-color: #5b39c9;
}

.ps-customer-tab.is-active {
  background: #5b39c9;
  color: #ffffff;
  border-color: #141021;
  box-shadow: 3px 3px 0 0 #141021;
}

.ps-customer-tab.is-active ::v-deep .v-icon {
  color: #ffffff !important;
}

.ps-customer--clean .ps-customer-tab.is-active {
  border-color: transparent;
  box-shadow: none;
}

.ps-customer--clean .ps-customer-btn--save {
  background: #5b39c9;
  color: #ffffff;
  border: none;
  box-shadow: none;
}

.ps-customer--clean .ps-customer-btn--save:hover {
  background: #4c2fb0;
  transform: none;
}

.ps-customer--clean .ps-customer-btn--save:active {
  transform: none;
  box-shadow: none;
}

.ps-customer--clean .ps-customer-btn--save ::v-deep .v-icon {
  color: #ffffff !important;
}

.ps-customer-tab:active {
  transform: translate(1px, 1px);
  box-shadow: none;
}

/* ---- Search: box + customer list ---- */
.ps-customer-search {
  width: 100%;
}

.ps-customer-searchbox {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 0 14px;
  min-height: 44px;
  border: 1px solid #e2e8f0;
  border-radius: 12px;
  background: #ffffff;
  transition: background-color 0.15s ease, border-color 0.15s ease;
}

.ps-customer-searchbox:focus-within {
  background: #ffffff;
  border-color: #cbd5e1;
}

.ps-customer-searchbox__icon {
  color: #64748b;
  flex-shrink: 0;
}

.ps-customer-searchbox__input {
  flex: 1;
  min-width: 0;
  border: none;
  outline: none;
  background: transparent;
  color: #141021;
  font-size: 14px;
  font-weight: 700;
  padding: 0;
}

.ps-customer-searchbox__input::placeholder {
  color: #94a3b8;
  font-weight: 500;
}

.ps-customer-searchbox__clear {
  width: 28px;
  height: 28px;
  border-radius: 8px;
  border: none;
  background: #e2e8f0;
  color: #64748b;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  flex-shrink: 0;
  transition: background-color 0.15s ease, color 0.15s ease;
}

.ps-customer-searchbox__clear:hover {
  background: #fecaca;
  color: #dc2626;
}

/* ---- Search feedback ---- */
.ps-customer-hint {
  margin: 10px 0 0;
  text-align: center;
  font-size: 12px;
  font-weight: 700;
  color: #94a3b8;
}

.ps-customer-hint--error {
  color: #dc2626;
  font-weight: 900;
}

.ps-customer-hint--muted {
  margin-top: 10px;
  font-size: 11px;
}

/* ---- Results list ---- */
.ps-customer-results {
  margin-top: 14px;
  max-height: 32vh;
  overflow-y: auto;
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding-right: 2px;
}

.ps-customer-result {
  display: flex;
  align-items: center;
  gap: 12px;
  width: 100%;
  padding: 10px 14px;
  border-radius: 14px;
  border: 1px solid #e2e8f0;
  background: #ffffff;
  text-align: left;
  cursor: pointer;
  transition: border-color 0.12s ease, background-color 0.12s ease;
}

.ps-customer-result:hover {
  border-color: #5b39c9;
  background: #f3f0fc;
}

.ps-customer-result.is-active {
  border-color: #5b39c9;
  background: #f3f0fc;
  box-shadow: 2px 2px 0 0 rgba(91, 57, 201, 0.25);
}

.ps-customer-result__avatar {
  width: 40px;
  height: 40px;
  border-radius: 12px;
  background: #f3f0fc;
  color: #5b39c9;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 14px;
  font-weight: 900;
  flex-shrink: 0;
}

.ps-customer-result__body {
  min-width: 0;
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 1px;
}

.ps-customer-result__name {
  color: #141021;
  font-size: 14px;
  font-weight: 900;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.ps-customer-result__phone {
  color: #94a3b8;
  font-size: 12px;
  font-weight: 700;
}

.ps-customer-result__check {
  flex-shrink: 0;
}

/* ---- Walk-in customer ---- */
.ps-customer-walkin {
  display: flex;
  align-items: center;
  gap: 10px;
  width: 100%;
  margin-top: 10px;
  padding: 11px 14px;
  border-radius: 14px;
  border: 1px dashed #94a3b8;
  background: transparent;
  text-align: left;
  cursor: pointer;
  transition: border-color 0.12s ease, background-color 0.12s ease;
}

.ps-customer-walkin:hover {
  border-color: #5b39c9;
  background: #f3f0fc;
}

.ps-customer-walkin__body {
  display: flex;
  flex-direction: column;
  gap: 1px;
  min-width: 0;
}

.ps-customer-walkin__title {
  font-size: 13px;
  font-weight: 900;
  color: #141021;
}

.ps-customer-walkin__hint {
  font-size: 11px;
  font-weight: 500;
  color: #94a3b8;
}

/* ---- Add customer form ---- */
.ps-customer-section {
  background: #ffffff;
  border: 1px solid #e2e8f0;
  border-radius: 14px;
  padding: 16px;
  margin-bottom: 14px;
}

.ps-customer-section__head {
  display: flex;
  align-items: center;
  gap: 8px;
}

.ps-customer-section__title {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  font-weight: 800;
  font-size: 14px;
  color: #334155;
}

.ps-customer-form-actions {
  display: flex;
  justify-content: flex-end;
  gap: 10px;
  margin-top: 18px;
}

.ps-customer-btn {
  min-height: 48px;
  padding: 0 26px;
  border-radius: 14px;
  font-size: 14px;
  font-weight: 900;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  transition: transform 0.15s ease, box-shadow 0.15s ease,
    background-color 0.15s ease, border-color 0.15s ease;
}

.ps-customer-btn--clear {
  background: #ffffff;
  color: #64748b;
  border: 1px solid #e2e8f0;
}

.ps-customer-btn--clear:hover {
  color: #5b39c9;
  border-color: #5b39c9;
}

.ps-customer-btn--save {
  background: #a3e635;
  color: #141021;
  border: none;
  box-shadow: 3px 3px 0 0 #141021;
}

.ps-customer-btn--save:hover {
  background: #8fd624;
  transform: translate(-1px, -1px);
}

.ps-customer-btn--save:active {
  transform: translate(1px, 1px);
  box-shadow: none;
}

/* ---- Logistic option (kept from previous design, brutal border) ---- */
.logistic-option {
  position: relative;
  border-radius: 14px;
  border: 1px solid #d1d5db !important;
  background: #ffffff;
  height: 110px;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: box-shadow 0.2s ease, transform 0.1s ease, border-color 0.2s ease;
}

.logistic-option:hover {
  transform: none;
  box-shadow: none;
  border-color: #d1d5db !important;
}

.logistic-option.selected {
  border-color: #d1d5db !important;
  box-shadow: none;
}

.logistic-content {
  text-align: center;
}

.logistic-title {
  margin-top: 6px;
  font-weight: 900;
  font-size: 13px;
  color: #141021;
}

.check {
  position: absolute;
  top: -5px;
  right: -5px;
  width: 20px;
  height: 20px;
  border-radius: 999px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: #5b39c9;
}

/* ---- Mobile ---- */
@media (max-width: 600px) {
  .ps-customer-search {
    max-width: 100%;
  }
}
</style>
